import React, { useState, useRef } from 'react';
import { Tooltip, Button, Form, Dropdown, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'font-awesome/css/font-awesome.min.css';
import {
  faClipboardCheck,
  faEllipsis,
  faFileExport,
  faFolderPlus,
  faKey,
  faPaperPlane,
  faPaste,
  faShare,
  faTrashCan,
  faUserGraduate
} from '@fortawesome/free-solid-svg-icons';
import AssignMenuModal from '../components/assignmentFloatingMenu/modals/AssignMenuModal';
import MoveMenuModal from './assignmentFloatingMenu/modals/MoveMenuModal';
import ShareMenuModal from './assignmentFloatingMenu/modals/ShareMenuModal';
import PublishMenuModal from './assignmentFloatingMenu/modals/PublishMenuModal';
import SubmitMenuModal from './assignmentFloatingMenu/modals/SubmitMenuModal';
import DuplicateMenuModal from './assignmentFloatingMenu/modals/DuplicateMenuModal';
import AddCollaboratorsModal from './assignmentFloatingMenu/modals/AddCollaboratorsModal';
import DeleteMenuModal from './assignmentFloatingMenu/modals/DeleteMenuModal';
import { useAssignmentContext } from '../context';
import CustomToast from '../../../components/Toast';
import Modal from '../../../components/Modal';
import { useExportXmlAssignmentsMutation } from '../../../features/authentication/assignApiSlice';
import { useSaveAssignmentMethodMutation } from '../../../features/authentication/assignmentApislice';
import { DashboardProvider, useDashboardContext } from '../../dashboard/context';
import appConfig from '../../../app-config';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from '../../../features/authentication/authSlice';
import assignMethodImg from '../../../assets/images/personWhiteboard.png';

const baseUrl = appConfig.apiBaseUrl;
const AssignmentFloatingMenu = () => {
  const token = useSelector(selectCurrentToken);
  const [exportAssignment] = useExportXmlAssignmentsMutation();
  const [assignAssignmentsModal, setAssignAssignmentsModal] = useState();
  const [moveAssignmentsModal, setMoveAssignmentsModal] = useState();
  const [shareAssignmentsModal, setShareAssignmentsModal] = useState();
  const [publishAssignmentsModal, setPublishAssignmentsModal] = useState();
  const [submitAssignmentsModal, setSubmitAssignmentsModal] = useState();
  const [duplicateAssignmentsModal, setDuplicateAssignmentsModal] = useState();
  const [addAssignmentCollaboratorsModal, setAddAssignmentCollaboratorsModal] = useState();
  const [deleteAssignmentsModal, setDeleteAssignmentsModal] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const selectedAssignment = useAssignmentContext();
  const { setAssignedData } = useAssignmentContext();
  const { setAssignedViaUpdate } = useAssignmentContext();
  const getData = useDashboardContext();
  const [saveAssignmentMethod, saveAssignmentMethodResults] = useSaveAssignmentMethodMutation();
  const assignmentMethodRef = useRef(1);
  const [showAssignmentMethodModal, setShowAssignmentMethodModal] = useState(false);

  const floatingMenu = [
    {
      id: 1,
      name: 'Assign',
      tooltip: 'Assign assignments to multiple students and/or yourself',
      icon: <FontAwesomeIcon icon={faUserGraduate} />,
      modal: () => handleAssignAssignment()
    },
    {
      id: 2,
      name: 'Move',
      tooltip: 'Move assignments to another folder',
      icon: <FontAwesomeIcon icon={faFolderPlus} />,
      modal: () => setMoveAssignmentsModal(!moveAssignmentsModal)
    },
    {
      id: 3,
      name: 'Export',
      tooltip: 'Export assignments for sharing',
      icon: <FontAwesomeIcon icon={faFileExport} />
    },
    {
      id: 4,
      name: 'Send Copy',
      tooltip: 'Share assignments with other teachers',
      icon: <FontAwesomeIcon icon={faShare} />,
      modal: () => setShareAssignmentsModal(!shareAssignmentsModal)
    },
    {
      id: 5,
      name: 'Publish',
      tooltip: 'Publish an assignment as a Public Assignment (only one can be checked)',
      icon: <FontAwesomeIcon icon={faPaperPlane} />,
      modal: () => setPublishAssignmentsModal(!publishAssignmentsModal)
    },
    {
      id: 6,
      name: 'Submit',
      tooltip: 'Submit an assignment as a Benchmark (only one can be checked)',
      icon: <FontAwesomeIcon icon={faClipboardCheck} />,
      modal: () => setSubmitAssignmentsModal(!submitAssignmentsModal)
    },
    {
      id: 7,
      name: 'Duplicate',
      tooltip: 'Duplicate an assignment',
      icon: <FontAwesomeIcon icon={faPaste} />,
      modal: () => setDuplicateAssignmentsModal(!duplicateAssignmentsModal)
    },
    {
      id: 8,
      name: 'Manage Access',
      tooltip: 'Add/Edit Collaborator access',
      icon: <FontAwesomeIcon icon={faKey} />,
      modal: () => setAddAssignmentCollaboratorsModal(!addAssignmentCollaboratorsModal)
    },
    {
      id: 9,
      name: 'Delete',
      tooltip: 'Delete assignments (only unassigned assignments can be deleted)',
      icon: <FontAwesomeIcon icon={faTrashCan} />,
      modal: () => setDeleteAssignmentsModal(!deleteAssignmentsModal)
    }
  ];

  const exportAssignmentMenu = async () => {
    try {
      const option = selectedAssignment.assignedData.map((assignment) => {
        return assignment.assignmentId;
      });
      await exportAssignment(option).unwrap();
    } catch (error) {
      if (error.data) {
        const XMLData = error.data;
        const blob = new Blob([XMLData], { type: 'text/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = 'CastleXMLAssignments.xml';
        link.download = fileName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const floatingMenuHandler = (value) => {
    if (selectedAssignment.assignedData.length > 0) {
      if (value.id !== 3) {
        if (value.id === 4) {
          getData.setSelectRecipient(true);
        } else {
          if (value.id === 5 || value.id === 6) {
            selectedAssignment.assignedData.length === 1
              ? value.id === 6 || value.id === 5
                ? validateSubmitAssignmentAsync(value)
                : value.modal()
              : CustomToast('Please check only one assignment', 'error');
          } else {
            value.modal();
          }
        }
      } else {
        exportAssignmentMenu();
      }
    } else {
      CustomToast('You have not selected any assignments.', 'info');
    }
  };

  const validateSubmitAssignmentAsync = async (value) => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        const options = {
          assignmentId: selectedAssignment.assignedData.map((assignment) => {
            return assignment.assignmentId;
          })
        };
        let url = baseUrl + '/Assignment/ValidateSubmitAssignment';
        url += '?assignmentId=' + options.assignmentId;
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        const response = await fetch(url, { headers, method: 'GET' });
        const errorMsg = await response.text();
        const result = response.status;
        if (result === 200) {
          value.modal();
          setIsLoading(false);
          setIsDisabled(false);
        } else {
          CustomToast(`${errorMsg}`, 'warning');
          setIsLoading(false);
          setIsDisabled(false);
        }
      } catch (ex) {
        console.log(ex);
        setIsLoading(false);
        setIsDisabled(false);
      }
    }
  };

  const handleAssignAssignment = () => {
    if (
      selectedAssignment.assignedData.every(
        (assignment) => assignment.assignedVia === selectedAssignment.assignedData[0].assignedVia
      )
    ) {
      if (selectedAssignment.assignedData[0].assignedVia) {
        setAssignAssignmentsModal(!assignAssignmentsModal);
      } else {
        setShowAssignmentMethodModal(true);
      }
    } else {
      CustomToast(
        'One or more of the selected assignments have a different method of assigning.  Please only select assignments with the same "Assigned Via" method.',
        'info'
      );
    }
  };

  async function handleSaveAssignmentMethod() {
    try {
      await saveAssignmentMethod({
        assignmentIds: selectedAssignment.assignedData.map((assignment) => {
          return assignment.assignmentId;
        }),
        assignedViaId: assignmentMethodRef.current.value
      }).unwrap();

      var tempAssignedData = selectedAssignment.assignedData.map((assignment) => {
        return { ...assignment, assignedVia: parseInt(assignmentMethodRef.current.value) };
      });
      setAssignedViaUpdate(true);
      setAssignedData(tempAssignedData);

      setShowAssignmentMethodModal(false);
      setAssignAssignmentsModal(true);
      CustomToast(
        `The assignment method${
          selectedAssignment.assignedData.length === 1 ? ' has' : 's have'
        } been updated`,
        'success'
      );
    } catch (ex) {
      CustomToast(
        `Something went wrong, could not update assignment method${
          selectedAssignment.assignedData.length === 1 ? '' : 's'
        }`,
        'error'
      );
      console.log(ex);
    }
  }
  return (
    <>
      <div className="floating-menu-in">
        {floatingMenu.map((value, index) => (
          <OverlayTrigger
            key={`float-top-${index}`}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-top-${value.id}`} className="custom-tooltip">
                {value.tooltip}
              </Tooltip>
            }>
            <button
              key={`floating-Menu-btn-${index}`}
              id={`floating-menu-${value.id}`}
              onClick={() => floatingMenuHandler(value)}>
              {value.icon}
              {value.name}
            </button>
          </OverlayTrigger>
        ))}

        <Dropdown>
          <Dropdown.Toggle>
            <FontAwesomeIcon icon={faEllipsis} />
            More
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {floatingMenu.map((value, index) => (
              <Dropdown.Item
                key={`float-menu-${index}`}
                onClick={
                  selectedAssignment.assignedData.length > 0
                    ? value.id !== 3
                      ? value.modal
                      : () => exportAssignmentMenu()
                    : () => {
                      CustomToast('You have not selected any assignments.', 'info');
                    }
                }
                disabled={isDisabled}>
                {value.icon} {value.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* Float 1 */}
        {assignAssignmentsModal && (
          <AssignMenuModal
            modalData={assignAssignmentsModal}
            setModalData={setAssignAssignmentsModal}
          />
        )}
        {/* Float 1 */}
        {/* Float 2 */}
        {moveAssignmentsModal && (
          <MoveMenuModal
            modalData={moveAssignmentsModal}
            setModalData={setMoveAssignmentsModal}
            fromMoreActions={false}
          />
        )}
        
        {/* Float 2 */}
        {/* Float 4 */}
        <ShareMenuModal
          assignmentId={selectedAssignment?.assignedData?.map((item) => item.assignmentId)}
        />
        {/* Float 4 */}
        {/* Float 5 */}
        <PublishMenuModal
          modalData={publishAssignmentsModal}
          setModalData={setPublishAssignmentsModal}
          fromMoreActions={false}
        />
        {/* Float 5 */}
        {/* Float 6 */}
        <SubmitMenuModal
          modalData={submitAssignmentsModal}
          setModalData={setSubmitAssignmentsModal}
          fromMoreActions={false}
        />
        {/* Float 6 */}
        {/* Float 7 */}
        <DuplicateMenuModal
          modalData={duplicateAssignmentsModal}
          setModalData={setDuplicateAssignmentsModal}
          fromMoreActions={false}
        />
        {/* Float 7 */}
        {/* Float 8 */}
        {addAssignmentCollaboratorsModal && (
          <AddCollaboratorsModal
            modalData={addAssignmentCollaboratorsModal}
            setModalData={setAddAssignmentCollaboratorsModal}
            fromMoreActions={false}
          />
        )}
        {/* Float 8 */}
        {/* Float 9 */}
        {deleteAssignmentsModal && (
          <DeleteMenuModal
            modalData={deleteAssignmentsModal}
            setModalData={setDeleteAssignmentsModal}
            fromMoreActions={false}
          />
        )}
        {/* Float 9 */}
      </div>

      <Modal
        contentWidth="40%"
        isOpen={showAssignmentMethodModal}
        titleName={'Assignment Method'}
        onClose={() => {
          setShowAssignmentMethodModal(false);
        }}
        disableBackdropClose={true}>
        <div className="d-flex flex-column align-items-center">
          <div
            className="mb-3"
            style={{
              backgroundImage: `url(${assignMethodImg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100%',
              height: '130px',
              width: '199px'
            }}></div>
          <div>
            {selectedAssignment.assignedData.length === 1
              ? 'How will you assign this assignment?'
              : 'How will you assign these assignments'}
          </div>
          <div className="d-flex flex-row w-50 my-4 align-items-center justify-content-evenly">
            <span>Assigned Via</span>
            <Form.Select ref={assignmentMethodRef} style={{ maxWidth: '50%' }} defaultValue="1">
              {[
                { assignedViaId: 1, name: 'Castle' },
                { assignedViaId: 2, name: 'Google Classroom' },
                { assignedViaId: 3, name: 'Schoology' },
                { assignedViaId: 4, name: 'Canvas' }
              ].map((item, index) => {
                return (
                  <option key={`assignmentMethodKey${index}`} value={item.assignedViaId}>
                    {item.name}
                  </option>
                );
              })}
            </Form.Select>
          </div>
          <div className="d-flex flex-row w-50 justify-content-evenly">
            <Button
              variant="castleGray"
              style={{ width: '45%' }}
              onClick={() => {
                setShowAssignmentMethodModal(false);
              }}>
              Cancel
            </Button>
            <Button
              variant="castleRed"
              style={{ width: '45%' }}
              onClick={(e) => handleSaveAssignmentMethod(e)}
              disabled={saveAssignmentMethodResults.isLoading}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const Layout = () => {
  return (
    <DashboardProvider>
      <AssignmentFloatingMenu />
    </DashboardProvider>
  );
};

export default Layout;
